import { api } from './api';

/**
 * Création et modification de dossier (11/10/2026) — client de
 * `/espace/dossiers/*` (session du portail). Cf. docs/creation-dossier.md.
 */

export type Produit = 'APP' | 'COMPTA' | 'IMMO' | 'PREVI';
export const PRODUITS: Produit[] = ['APP', 'COMPTA', 'IMMO', 'PREVI'];

export const NOMS_PRODUITS: Record<Produit, string> = {
  APP: 'SEmplyApp',
  COMPTA: 'SEmplyCompta',
  IMMO: 'SEmplyImmo',
  PREVI: 'SEmplyPrevi',
};

export interface OptionQuestion {
  valeur: string;
  libelle: string;
  aide?: string;
}

export interface Question {
  cle: string;
  groupe: string;
  etape: string;
  position: number;
  libelle: string;
  aide?: string | null;
  type: 'TEXTE' | 'NOMBRE' | 'DATE' | 'CHOIX' | 'BOOLEEN';
  options?: OptionQuestion[] | null;
  obligatoire?: boolean;
  defaut?: unknown;
  surchargeAutorisee?: boolean;
  datee?: boolean;
  /** Produits alimentés par la question. */
  correspondances: Produit[];
}

export interface ProduitPropose {
  produit: Produit;
  clientId: string;
  nom: string;
  description: string | null;
  url: string | null;
  lance: boolean;
  raccorde: boolean;
  dejaOuvert: boolean;
  souscrit: boolean;
  /** Repère de formule : `payant`, `free` (plancher gratuit), ou null. */
  formule: string | null;
  quota: { limite: number | null | undefined; utilises: number };
  quotaAtteint: boolean;
  exigeSiret: boolean;
}

export interface Etape {
  id: string;
  libelle: string;
  groupe: string;
}

export interface ResultatProduit {
  produit: Produit;
  statut: 'A_FAIRE' | 'OK' | 'ECHEC';
  erreur: string | null;
  tentatives: number;
}

export interface Creation {
  id: string;
  statut: 'BROUILLON' | 'PAIEMENT' | 'CREATION' | 'TERMINEE' | 'PARTIELLE' | 'ABANDONNEE';
  produits: Produit[];
  companyId: string | null;
  etape: string | null;
  identite: Record<string, string>;
  identiteRequise: boolean;
  reponses: Record<string, unknown>;
  valeurs: Record<string, unknown>;
  etapes: Etape[];
  questions: Question[];
  manquantes: string[];
  reprises: { cle: string; libelle: string; type: Question['type']; options?: OptionQuestion[] | null; valeur: unknown }[];
  resultats: ResultatProduit[];
  retourUrl: string | null;
  paiements: unknown[];
  expireLe: string;
}

/** Étape « Formules » (11/10/2026). */
export interface PlanVue {
  id: string;
  name: string;
  product: Produit;
  type: string;
  includes: Produit[];
  priceMonthly: number;
  stripePriceId: string | null;
  description: string | null;
}

export interface LigneFormule {
  produit: Produit;
  nom: string;
  etat: 'OK' | 'QUOTA_ATTEINT' | 'A_CHOISIR';
  formuleActuelle: { planId: string; nom: string | null; quantite: number; payante: boolean } | null;
  quota: { limite: number | null | undefined; utilises: number };
  formules: PlanVue[];
  ajoutDossier: { stripePriceId: string; quantiteActuelle: number } | null;
  changement: boolean;
}

export interface EtatFormules {
  lignes: LigneFormule[];
  offresGroupees: PlanVue[];
  pret: boolean;
  venteOuverte: boolean;
  espacePrincipal: boolean;
  /** Propriétaire ou administrateur de l'espace du dossier (29/10/2026). */
  gestionnaire: boolean;
  /** Qui est facturé, et l'échéance commune de l'espace (29/10/2026). */
  facturation: Facturation & { spaceId: string; espace: string | null };
}

/** Le profil de facturation d'un espace (29/10/2026) : un espace = un payeur. */
export interface ProfilFacturation {
  type: 'PRO' | 'PARTICULIER';
  name: string;
  firstName: string | null;
  lastName: string | null;
  siren: string | null;
  siret: string | null;
  vatNumber: string | null;
  addressLine1: string;
  addressLine2: string | null;
  postCode: string;
  city: string;
  country: string;
  email: string;
  /** Adresse de facturation électronique (BT-49, 01/11/2026) : type EAS et valeur. */
  adresseElectroniqueScheme: string | null;
  adresseElectroniqueValeur: string | null;
  /** Autres destinataires de l'avis de facture (01/11/2026), cinq au plus. */
  emailsCopie: string[];
  updatedAt?: string;
}

export interface Facturation {
  profil: ProfilFacturation | null;
  abonnementVivant: boolean;
  echeance: string | null;
}

export interface ApercuChangement {
  product: Produit;
  plan: { id: string; name: string; priceMonthly: number };
  quantity: number;
  /** Rien n'est prélevé tout de suite depuis le 29/10/2026 : toujours 0. */
  dueNow: number;
  /** Produit ajouté à l'abonnement de l'espace (sinon : modification). */
  ajout?: boolean;
  /** Prorata jusqu'à l'échéance commune, ajouté à la prochaine facture (négatif : crédit). */
  prorata?: number;
  /** Total de la prochaine facture de l'espace, prorata compris. */
  prochaineFacture?: number;
  monthlyAfter: number;
  currency: string;
  prorationDate: number;
  currentPeriodEnd: string | null;
  unchanged: boolean;
}

/** Établissement trouvé par SIRET (annuaire des entreprises). */
export interface FicheEtablissement {
  siret: string;
  siren: string;
  denomination: string | null;
  formeJuridique: string | null;
  /** Code de catégorie juridique INSEE (14/10/2026). */
  formeJuridiqueCode: string | null;
  adresse: string | null;
  /** Adresse en zones (13/10/2026). */
  numeroVoie: string | null;
  indiceRepetition: string | null;
  typeVoie: string | null;
  libelleVoie: string | null;
  complementAdresse: string | null;
  codePostal: string | null;
  ville: string | null;
  apeCode: string | null;
  apeLibelle: string | null;
  actif: boolean;
}

export interface QuestionFormulaire extends Question {
  valeur: unknown;
  propres: Partial<Record<Produit, unknown>>;
  historique: { portee: string; effetLe: string | null; valeur: unknown }[];
}

export interface Formulaire {
  dossier: Record<string, string | null> & { id: string; name: string; type: string };
  produitsOuverts: Produit[];
  peutModifier: boolean;
  etapes: Etape[];
  questions: QuestionFormulaire[];
  journal: { id: string; le: string; changements: unknown; produits: unknown }[];
  /** Changements pas encore reçus par un produit (EN_ATTENTE), refus à signaler (DIVERGENCE). */
  synchros: SynchroProduit[];
}

export interface SynchroProduit {
  product: Produit;
  statut: 'EN_ATTENTE' | 'DIVERGENCE';
  cles: string[];
  motif: string | null;
  tentatives: number;
  derniereTentative: string | null;
  derniereErreur: string | null;
  creeLe: string;
}

export type ResultatSynchro = { resultat: 'RIEN' | 'APPLIQUE' | 'EN_ATTENTE' | 'DIVERGENCE'; motif?: string };

export interface Changement {
  cle: string;
  valeur: unknown;
  effetLe?: string | null;
  portee?: Produit | null;
}

export interface ResultatSimulation {
  produit: Produit;
  resultat: 'ACCEPTE' | 'REFUSE' | 'INDISPONIBLE';
  motif?: string;
  effet?: string;
  datePossible?: string;
}

export interface DroitMembre {
  userId: string;
  email: string;
  nom: string | null;
  role: 'OWNER' | 'ADMIN' | 'MEMBER';
  profil: 'STANDARD' | 'LECTURE_SEULE';
  peutModifier: boolean;
  droitAccorde: boolean;
  modifiable: boolean;
}

const BASE = '/espace/dossiers';

export const dossiers = {
  accueil: () => api.get<{ afficher: boolean }>(`${BASE}/accueil`),
  accueilVu: () => api.post<{ ok: true }>(`${BASE}/accueil`),
  produits: (dossier?: string | null, espace?: string | null) => {
    const q = new URLSearchParams();
    if (dossier) q.set('dossier', dossier);
    if (espace) q.set('espace', espace);
    const chaine = q.toString();
    return api.get<ProduitPropose[]>(`${BASE}/produits${chaine ? `?${chaine}` : ''}`);
  },
  creer: (corps: {
    produits: Produit[];
    companyId?: string | null;
    retourUrl?: string | null;
    lienPrevi?: string | null;
    /** Nouveau dossier : l'espace qui le portera et sera facturé (29/10/2026). */
    spaceId?: string | null;
  }) =>
    api.post<Creation>(`${BASE}/creations`, corps),
  lire: (id: string) => api.get<Creation>(`${BASE}/creations/${id}`),
  enregistrer: (id: string, corps: { identite?: Record<string, unknown>; reponses?: Record<string, unknown>; etape?: string }) =>
    api.patch<Creation>(`${BASE}/creations/${id}`, corps),
  terminer: (id: string) => api.post<Creation>(`${BASE}/creations/${id}/terminer`),
  reessayer: (id: string, produit: Produit) =>
    api.post<Creation>(`${BASE}/creations/${id}/produits/${produit.toLowerCase()}/reessayer`),
  abandonner: (id: string) => api.delete<{ ok: true }>(`${BASE}/creations/${id}`),
  etablissement: (siret: string) =>
    api.get<FicheEtablissement>(`${BASE}/siret/${encodeURIComponent(siret.replace(/[\s.]/g, ''))}`),
  formules: (id: string) => api.get<EtatFormules>(`${BASE}/creations/${id}/formules`),
  /** Session Stripe, ou — l'espace ayant déjà son abonnement — un aperçu d'ajout à confirmer. */
  payer: (id: string, corps: { priceId: string; quantite?: number; code?: string | null }) =>
    api.post<{ sessionId: string; clientSecret: string } | { ajout: true; apercu: ApercuChangement }>(
      `${BASE}/creations/${id}/paiement`,
      corps,
    ),
  apercuChangement: (id: string, corps: { priceId: string; quantite?: number }) =>
    api.post<ApercuChangement>(`${BASE}/creations/${id}/changement/apercu`, corps),
  changer: (id: string, corps: { priceId: string; quantite?: number; prorationDate?: number }) =>
    api.post<unknown>(`${BASE}/creations/${id}/changement`, corps),
  formulaire: (companyId: string) => api.get<Formulaire>(`${BASE}/${companyId}/formulaire`),
  simuler: (companyId: string, changements: Changement[]) =>
    api.post<{ produits: ResultatSimulation[] }>(`${BASE}/${companyId}/simuler`, { changements }),
  appliquer: (companyId: string, changements: Changement[], produitsCoches: Produit[]) =>
    api.post<{ produits: { produit: Produit; coche: boolean; resultat: string; motif?: string }[] }>(
      `${BASE}/${companyId}/appliquer`,
      { changements, produitsCoches },
    ),
  synchroniser: (companyId: string, produit: Produit) =>
    api.post<ResultatSynchro>(`${BASE}/${companyId}/synchroniser/${produit.toLowerCase()}`),
  acquitter: (companyId: string, produit: Produit) =>
    api.delete<{ ok: true }>(`${BASE}/${companyId}/alertes/${produit.toLowerCase()}`),
  retirerValeurPropre: (companyId: string, cle: string, produit: Produit) =>
    api.delete<{ ok: true; synchro?: ResultatSynchro }>(`${BASE}/${companyId}/valeurs-propres/${encodeURIComponent(cle)}/${produit.toLowerCase()}`),
  identite: (companyId: string, corps: Record<string, string>) =>
    api.patch<Record<string, unknown>>(`${BASE}/${companyId}/identite`, corps),
  droits: (companyId: string) => api.get<DroitMembre[]>(`${BASE}/${companyId}/droits`),
  accorder: (companyId: string, userId: string) => api.put<DroitMembre[]>(`${BASE}/${companyId}/droits/${userId}`),
  retirer: (companyId: string, userId: string) => api.delete<DroitMembre[]>(`${BASE}/${companyId}/droits/${userId}`),
};

/** Valeur affichée d'une réponse (récapitulatif, valeurs reprises). */
export function libelleValeur(q: Pick<Question, 'type' | 'options'>, valeur: unknown): string {
  if (valeur === undefined || valeur === null || valeur === '') return '—';
  if (q.type === 'BOOLEEN') return valeur ? 'Oui' : 'Non';
  if (q.type === 'CHOIX') return q.options?.find((o) => o.valeur === valeur)?.libelle ?? String(valeur);
  if (q.type === 'DATE' && typeof valeur === 'string') {
    const [a, m, j] = valeur.split('-');
    return `${j}/${m}/${a}`;
  }
  return String(valeur);
}

/** Profil de facturation d'un espace (29/10/2026). */
export const facturation = {
  /** L'espace par défaut du compte, le rôle qu'on y a et qui il facture. */
  compte: (spaceId?: string | null) =>
    api.get<{ space: { id: string; name: string } | null; role: string; facturation: Facturation }>(
      `/espace/billing/account${spaceId ? `?spaceId=${encodeURIComponent(spaceId)}` : ''}`,
    ),
  lire: (spaceId: string) =>
    api.get<{ profil: ProfilFacturation | null }>(`/espace/billing/profil?spaceId=${encodeURIComponent(spaceId)}`),
  /** Factures et avoirs émis par Vanna pour l'espace (29/10/2026, lot 4). */
  factures: (spaceId: string) =>
    api.get<FactureEspace[]>(`/espace/billing/factures?spaceId=${encodeURIComponent(spaceId)}`),
  enregistrer: (
    spaceId: string,
    profil: Omit<ProfilFacturation, 'siren' | 'updatedAt'>,
    confirmerChangementSiren = false,
  ) =>
    api.put<{ profil: ProfilFacturation }>('/espace/billing/profil', {
      spaceId,
      profil,
      confirmerChangementSiren,
    }),
};

/** Déplacement d'un dossier vers un autre espace (29/10/2026, lot 3). */
export interface ManqueDeplacement {
  produit: Produit;
  nom: string;
  raison: 'FORMULE_REQUISE' | 'QUOTA_ATTEINT';
}

export interface EtatDeplacement {
  dossier: { id: string; name: string; espace: string };
  gestionnaire: boolean;
  enCours: { id: string; effectiveAt: string; requestedAt: string; toSpace: { id: string; name: string } } | null;
  destinations: { id: string; name: string }[];
  /** Date à laquelle un déplacement demandé maintenant prendrait effet. */
  echeance: string;
}

export const deplacements = {
  lire: (companyId: string) => api.get<EtatDeplacement>(`${BASE}/${companyId}/deplacement`),
  programmer: (companyId: string, spaceId: string) =>
    api.post<{ id: string; effectiveAt: string; vers: { id: string; name: string } }>(`${BASE}/${companyId}/deplacement`, {
      spaceId,
    }),
  annuler: (companyId: string) => api.delete<{ annule: true }>(`${BASE}/${companyId}/deplacement`),
};

/** Une facture (ou un avoir) de l'espace, émise par Vanna (29/10/2026, lot 4). */
export interface FactureEspace {
  id: string;
  kind: 'FACTURE' | 'AVOIR';
  number: string;
  issuedAt: string;
  amountHt: number;
  amountTva: number;
  amountTtc: number;
  status: 'PAYEE' | 'REMBOURSEE';
  pdfDisponible: boolean;
  creditedInvoice: { number: string } | null;
}
